import { useEffect, useState } from "react";

import {
  getAbsences,
  getBodyWeights,
  getInsights,
  getMainLifts,
  getStatsSummary,
  applyForCoach,
  getFriends,
  getAthletes,
  getMyCoach,
  leaveCoach,
  type Absence,
  type Athlete,
  type Friend,
  type Insight,
  type MyCoach,
  type MainLift,
  type StatsSummary,
} from "../lib/api";
import { wordFor } from "../lib/format";
import { absenceAt, human, iso } from "../lib/calendar";
import { haptic, notify, tg } from "../lib/telegram";
import PixelField from "../components/PixelField";
import { MuscleRadar, WeeklyBars, coarseAxes } from "../components/ProfileCharts";
import { useUser } from "../store/user";
import InsightList from "../components/InsightList";
import VolumeLandmarks from "../components/VolumeLandmarks";

const ROLE: Record<string, string> = {
  athlete: "Атлет",
  coach: "Тренер",
  admin: "Владелец",
};

const fmt = (v: number) =>
  Number(v.toFixed(1)).toLocaleString("ru-RU", { maximumFractionDigits: 1 });

/**
 * Профиль.
 *
 * Здесь только то, что про меня: кто я, как хожу, к чему пришёл. Друзья,
 * ученики, максимумы и параметры — за плитками-входами: раньше они лежали
 * восемью карточками подряд, и профиль читался как свалка
 * (18-profile-parameters.md §2).
 */
export default function Profile({
  onOpenExercise,
  onOpenFriends,
  onOpenAthletes,
  onOpenMaxes,
  onOpenSettings,
  onOpenCalendar,
  onOpenBody,
}: {
  onOpenExercise: (exerciseId: number) => void;
  onOpenFriends: () => void;
  onOpenAthletes: () => void;
  onOpenMaxes: () => void;
  onOpenSettings: () => void;
  onOpenCalendar: () => void;
  onOpenBody: () => void;
}) {
  const { me } = useUser();
  const [summary, setSummary] = useState<StatsSummary | null>(null);
  const [lifts, setLifts] = useState<MainLift[] | null>(null);
  const [insights, setInsights] = useState<Insight[]>([]);
  const [photoBroken, setPhotoBroken] = useState(false);
  // Тренерский слой: кто меня ведёт и (для роли coach) сколько у меня учеников.
  const [coach, setCoach] = useState<MyCoach | null>(null);
  const [athletes, setAthletes] = useState<Athlete[] | null>(null);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [absences, setAbsences] = useState<Absence[]>([]);
  const [body, setBody] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getStatsSummary().then(setSummary).catch(() => setSummary(null));
    getMainLifts().then(setLifts).catch(() => setLifts([]));
    // Пустой список — нормальный и частый ответ, поэтому раздела просто не будет.
    getInsights().then(setInsights).catch(() => setInsights([]));
    getFriends().then(setFriends).catch(() => setFriends([]));
    // Перерыв переехал сюда с главной: это состояние человека, а не сегодняшнее
    // действие (19-home-and-bot-cards.md §3).
    getAbsences().then(setAbsences).catch(() => setAbsences([]));
    getBodyWeights(90)
      .then((rows) => setBody(rows.length ? rows[rows.length - 1].weight_kg : null))
      .catch(() => setBody(null));
  }, []);

  useEffect(() => {
    getMyCoach().then(setCoach).catch(() => setCoach(null));
    if (me?.role === "coach" || me?.role === "admin") {
      getAthletes().then(setAthletes).catch(() => setAthletes([]));
    }
  }, [me?.role]);

  async function becomeCoach() {
    if (busy) return;
    haptic("medium");
    setBusy(true);
    try {
      await applyForCoach();
      notify("success");
    } catch {
      notify("error");
    }
    setBusy(false);
  }

  async function leave() {
    if (busy) return;
    haptic();
    setBusy(true);
    try {
      await leaveCoach();
      setCoach({ coach_name: null, visible_to_coach: [] });
      notify("success");
    } catch {
      notify("error");
    }
    setBusy(false);
  }

  const name = me?.first_name?.trim() || me?.username || "Спортсмен";
  // Фото из Telegram: своё лицо узнаётся быстрее имени. Нет фото или не
  // загрузилось — первая буква имени, а не серый силуэт.
  const photo = photoBroken ? null : tg()?.initDataUnsafe?.user?.photo_url ?? null;
  // На плитке максимумов — лучшее введённое число: оно отвечает на «а что там»
  // не открывая экран. Ничего не введено — плитка зовёт вписать.
  const best = lifts?.reduce<number | null>(
    (top, lift) => (lift.one_rm_kg !== null && (top === null || lift.one_rm_kg > top) ? lift.one_rm_kg : top),
    null,
  ) ?? null;
  const coaching = me?.role === "coach" || me?.role === "admin";
  const away = absenceAt(absences, iso(new Date()));

  return (
    <div className="screen">
      <div className="header">
        <span className="eyebrow">Профиль</span>
        <span className="eyebrow">{ROLE[me?.role ?? "athlete"] ?? me?.role}</span>
      </div>

      <div className="me">
        <PixelField className="me__field" seed={5} />
        {photo ? (
          <img
            className="me__avatar"
            src={photo}
            alt=""
            referrerPolicy="no-referrer"
            onError={() => setPhotoBroken(true)}
          />
        ) : (
          <span className="me__avatar" aria-hidden="true">
            {name.slice(0, 1).toUpperCase()}
          </span>
        )}
        <div className="me__body">
          <h1 className="title">{name}</h1>
          {me?.username && <span className="me__meta">@{me.username}</span>}
        </div>
      </div>

      <div className="tiles">
        <div className="tile">
          <span className="tile__value">{summary?.total_workouts ?? "—"}</span>
          <span className="tile__key">
            {wordFor(summary?.total_workouts ?? 0, "тренировка", "тренировки", "тренировок")}
            {" за 12 недель"}
          </span>
        </div>
        <div className="tile">
          <span className="tile__value">
            {summary === null
              ? "—"
              : `${Math.round(summary.total_volume_kg / 1000).toLocaleString("ru-RU")} т`}
          </span>
          <span className="tile__key">суммарный объём</span>
        </div>
      </div>

      {away && (
        <button
          className="card card--tap"
          onClick={() => {
            haptic();
            onOpenCalendar();
          }}
        >
          <span className="card__title">Перерыв</span>
          <span className="lead" style={{ margin: 0 }}>
            Отмечено с {human(away.starts_on)}
            {away.ends_on ? ` по ${human(away.ends_on)}` : ", конец не задан"}.
            Напоминания молчат, серия недель не прерывается.
          </span>
        </button>
      )}

      {/* Раздела нет, когда сказать нечего: молчать важнее, чем заполнить экран. */}
      <InsightList items={insights} onOpen={onOpenExercise} />

      {summary && <WeeklyBars weeks={summary.weeks} />}
      {summary && <MuscleRadar axes={coarseAxes(summary.muscles)} />}
      {summary && <VolumeLandmarks rows={summary.weekly_sets ?? []} />}

      {/* Входы одинакового веса: человек выбирает раздел, а не читает список. */}
      <div className="tiles tiles--entries">
        <button
          className="tile tile--tap"
          onClick={() => {
            haptic();
            onOpenFriends();
          }}
        >
          <span className="tile__value">{friends.length}</span>
          <span className="tile__key">
            {friends.length === 0
              ? "друзья — позвать"
              : wordFor(friends.length, "друг", "друга", "друзей")}
          </span>
        </button>

        {coaching && (
          <button
            className="tile tile--tap"
            onClick={() => {
              haptic();
              onOpenAthletes();
            }}
          >
            <span className="tile__value">{athletes?.length ?? "—"}</span>
            <span className="tile__key">
              {(athletes?.length ?? 0) === 0
                ? "ученики — позвать"
                : wordFor(athletes?.length ?? 0, "ученик", "ученика", "учеников")}
            </span>
          </button>
        )}

        <button
          className="tile tile--tap"
          onClick={() => {
            haptic();
            onOpenMaxes();
          }}
        >
          <span className="tile__value">{best === null ? "—" : `${fmt(best)} кг`}</span>
          <span className="tile__key">
            {best === null ? "максимумы — вписать" : "лучший максимум"}
          </span>
        </button>

        <button
          className="tile tile--tap"
          onClick={() => {
            haptic();
            onOpenBody();
          }}
        >
          <span className="tile__value">{body === null ? "—" : `${body.toLocaleString("ru-RU")} кг`}</span>
          <span className="tile__key">{body === null ? "вес тела — записать" : "вес тела"}</span>
        </button>

        <button
          className="tile tile--tap"
          onClick={() => {
            haptic();
            onOpenSettings();
          }}
        >
          <span className="tile__value">Параметры</span>
          <span className="tile__key">
            {me?.theme === "light" ? "светлая тема" : "тёмная тема"}, уведомления
          </span>
        </button>
      </div>

      {/* Человек вправе в любой момент видеть, кто его ведёт и что тому видно
          (§14 ТЗ в редакции 16.09.2026). */}
      {coach?.coach_name && (
        <div className="card">
          <p className="card__title">Мой тренер</p>
          <div className="row">
            <span className="row__key">{coach.coach_name}</span>
            <button className="wo-btn" disabled={busy} onClick={() => void leave()}>
              отвязаться
            </button>
          </div>
          {coach.visible_to_coach.map((line) => (
            <div className="row" key={line}>
              <span className="row__key">{line}</span>
              <span className="row__val">видно</span>
            </div>
          ))}
        </div>
      )}

      {/* Роль тренера открывает доступ к чужим данным, поэтому выдаётся руками.
          Но путь к ней должен быть виден — иначе её можно получить только
          правкой в базе. */}
      {me?.role === "athlete" && (
        <div className="card">
          <p className="card__title">Ведёте учеников?</p>
          <p className="lead" style={{ margin: 0 }}>
            Роль тренера даёт назначать программы, видеть тренировки учеников и
            разбирать подходы. Заявка уходит владельцу приложения.
          </p>
          <button
            className="wo-btn"
            style={{ width: "100%", marginTop: 10 }}
            disabled={busy}
            onClick={() => void becomeCoach()}
          >
            Стать тренером
          </button>
        </div>
      )}

      <p className="footer">
        RACK · тренировки хранятся только у тебя, делиться ими можно лишь явным
        действием.
      </p>
    </div>
  );
}
